import { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import ZStudioNav from '@/pages/dashboard/apps/zstudio/components/ZStudioNav';
import { listZProducts, listZServices, type ZProductItem, type ZServiceItem } from '@/lib/zstudio';

export default function ZStudioQrPage() {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [products, setProducts] = useState<ZProductItem[]>([]);
  const [services, setServices] = useState<ZServiceItem[]>([]);
  const [tab, setTab] = useState<'product' | 'service' | 'custom'>('custom');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listZProducts().then(setProducts).catch(() => {});
    listZServices().then(setServices).catch(() => {});
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    setUrl(`${origin}/produits`);
  }, []);

  const download = () => {
    const canvas = wrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `zstudio-qr-${(label || 'code').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.png`;
    link.click();
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="mb-4">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-qr-code-line text-accent-500"></i>QR produit
        </h2>
        <p className="text-sm text-foreground-500 mt-1">Générez un QR code vers un produit, un service ou une page — à intégrer sur une affiche, un flyer ou une publicité (spec 35).</p>
      </div>
      <ZStudioNav />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit mb-4">
            {([['custom', 'Lien libre', 'ri-link'], ['product', 'Produit', 'ri-shopping-bag-3-line'], ['service', 'Service', 'ri-service-line']] as const).map(([k, l, ic]) => (
              <button key={k} type="button" onClick={() => setTab(k)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${tab === k ? 'bg-background-50 text-foreground-900' : 'text-foreground-500'}`}>
                <i className={ic}></i>{l}
              </button>
            ))}
          </div>

          <label className="block text-xs font-medium text-foreground-600 mb-1.5">Adresse cible (URL)</label>
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-300 mb-3" />

          <label className="block text-xs font-medium text-foreground-600 mb-1.5">Titre (optionnel)</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex : Robe traditionnelle" className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-primary-300 mb-3" />

          {tab === 'product' && (
            <div className="max-h-56 overflow-y-auto space-y-1.5">
              {products.map((p) => (
                <button key={p.id} type="button" onClick={() => { const origin = window.location.origin; setUrl(`${origin}/produits?product=${p.id}`); setLabel(p.name); }} className="w-full text-left px-3 py-2 rounded-lg border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer">
                  <span className="block text-xs font-medium text-foreground-800 truncate">{p.name}</span>
                  <span className="block text-[10px] text-foreground-500 truncate">{p.price != null ? `${p.price} ${p.currency || ''}` : '—'}</span>
                </button>
              ))}
            </div>
          )}
          {tab === 'service' && (
            <div className="max-h-56 overflow-y-auto space-y-1.5">
              {services.map((s) => (
                <button key={s.id} type="button" onClick={() => { const origin = window.location.origin; setUrl(`${origin}/services?service=${s.id}`); setLabel(s.name); }} className="w-full text-left px-3 py-2 rounded-lg border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer">
                  <span className="block text-xs font-medium text-foreground-800 truncate">{s.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 flex flex-col items-center justify-center">
          <div ref={wrapRef} className="p-4 bg-white rounded-xl border border-background-200/70">
            {url ? <QRCodeCanvas value={url} size={220} level="M" includeMargin={false} /> : <div className="w-[220px] h-[220px] flex items-center justify-center text-foreground-400 text-xs">Saisissez une URL</div>}
          </div>
          {label && <p className="text-sm font-medium text-foreground-800 mt-4 text-center">{label}</p>}
          <p className="text-[11px] text-foreground-400 mt-1 text-center break-all max-w-xs">{url}</p>
          <button type="button" onClick={download} disabled={!url} className="mt-4 flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-download-2-line"></i>Télécharger le QR (PNG)
          </button>
          <p className="text-[11px] text-foreground-400 mt-3 text-center">Le QR code peut être intégré à une affiche, un flyer ou une image publicitaire ZStudio.</p>
        </div>
      </div>
    </div>
  );
}